import React, { useState } from "react";
import { Search, ImageIcon } from "lucide-react";
import { asText, EmptyRow, formatCompact, formatCurrency, formatRoas, LoadingRow, StatusBadge, unwrapValue, useCxoResource } from "../shared";

const STATUS_OPTIONS = ["All", "ACTIVE", "PAUSED", "LEARNING"];
const FORMAT_OPTIONS = ["all", "video", "carousel", "single_image"];
const PLACEMENT_OPTIONS = ["All", "feed", "stories", "reels", "explore"];

const AdsetAdsTab: React.FC<{ campaignId: string; adsetId: string; baseParams: Record<string, any>; onOpenAd: (adId: string) => void }> = ({ adsetId, baseParams, onOpenAd }) => {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("All");
  const [format, setFormat] = useState("all");
  const [placement, setPlacement] = useState("All");
  const params = {
    ...baseParams,
    sort_by: "spend",
    order: "desc",
    limit: "25",
    offset: "0",
    ...(search ? { q: search } : {}),
    ...(status !== "All" ? { status } : {}),
    ...(format !== "all" ? { ad_format: format } : {}),
    ...(placement !== "All" ? { placement } : {}),
  };
  const { data, loading } = useCxoResource<any>("/cxo-dashboard/adsets", `${adsetId}/ads`, params, !!adsetId);
  const items: any[] = Array.isArray(data?.items) ? data.items : [];

  return (
    <div>
      <div className="flex flex-col sm:flex-row flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[180px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search ads..." className="w-full pl-8 pr-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 placeholder-gray-400" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-700 dark:text-gray-200">
          {STATUS_OPTIONS.map((o) => (
            <option key={o} value={o}>
              Status: {o}
            </option>
          ))}
        </select>
        <select value={format} onChange={(e) => setFormat(e.target.value)} className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-700 dark:text-gray-200 capitalize">
          {FORMAT_OPTIONS.map((o) => (
            <option key={o} value={o}>
              Format: {o}
            </option>
          ))}
        </select>
        <select value={placement} onChange={(e) => setPlacement(e.target.value)} className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-700 dark:text-gray-200">
          {PLACEMENT_OPTIONS.map((o) => (
            <option key={o} value={o}>
              Placement: {o}
            </option>
          ))}
        </select>
      </div>

      {loading && items.length === 0 ? (
        <LoadingRow label="Loading ads…" />
      ) : items.length === 0 ? (
        <EmptyRow label="No ads match these filters." />
      ) : (
        <div className="overflow-x-auto -mx-1">
          <table className="w-full min-w-[820px] text-sm">
            <thead>
              <tr className="text-left text-[11px] font-semibold text-gray-400 dark:text-gray-500 tracking-wide">
                <th className="pb-2 pl-1">AD NAME</th>
                <th className="pb-2">STATUS</th>
                <th className="pb-2">FORMAT</th>
                <th className="pb-2">PLACEMENT</th>
                <th className="pb-2">SPEND</th>
                <th className="pb-2">IMPRESSIONS</th>
                <th className="pb-2">REACH</th>
                <th className="pb-2">RESULTS</th>
                <th className="pb-2">COST/RESULT</th>
                <th className="pb-2">ROAS</th>
                <th className="pb-2 pr-1 text-right">PREVIEW</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={it.ad_id || i} className="border-t border-gray-50 dark:border-gray-800">
                  <td className="py-2.5 pl-1 whitespace-nowrap">
                    <button type="button" onClick={() => it.ad_id && onOpenAd(it.ad_id)} className="flex items-center gap-2 font-medium text-indigo-600 dark:text-indigo-400 hover:underline text-left">
                      {/* Confirmed live: there's no top-level `thumbnail_url`
                          on an ad item — it's nested under `preview`, same
                          image > thumbnail priority as the ad detail page's
                          own creative preview. */}
                      {it.preview?.image_url || it.preview?.thumbnail_url ? (
                        <img src={it.preview?.image_url || it.preview?.thumbnail_url} alt="" className="w-6 h-6 rounded object-cover shrink-0" />
                      ) : (
                        <span className="w-6 h-6 rounded bg-gray-100 dark:bg-gray-800 flex items-center justify-center shrink-0">
                          <ImageIcon size={12} className="text-gray-400" />
                        </span>
                      )}
                      {asText(it.name, "N/A")}
                    </button>
                  </td>
                  <td className="py-2.5">
                    <StatusBadge status={it.status} />
                  </td>
                  <td className="py-2.5 text-gray-600 dark:text-gray-300 capitalize">{asText(it.format, "N/A")}</td>
                  <td className="py-2.5 text-gray-600 dark:text-gray-300 capitalize">{asText(it.placement, "N/A")}</td>
                  <td className="py-2.5 text-gray-600 dark:text-gray-300">{formatCurrency(unwrapValue(it.spend ?? it.metrics?.spend), "₦0.00")}</td>
                  <td className="py-2.5 text-gray-600 dark:text-gray-300">{formatCompact(unwrapValue(it.impressions ?? it.metrics?.impressions), "0")}</td>
                  <td className="py-2.5 text-gray-600 dark:text-gray-300">{formatCompact(unwrapValue(it.reach ?? it.metrics?.reach), "0")}</td>
                  <td className="py-2.5 text-gray-600 dark:text-gray-300">{formatCompact(unwrapValue(it.results ?? it.metrics?.results), "0")}</td>
                  <td className="py-2.5 text-gray-600 dark:text-gray-300">{formatCurrency(unwrapValue(it.cost_per_result ?? it.metrics?.cost_per_result), "₦0.00")}</td>
                  <td className="py-2.5 font-semibold text-emerald-600 dark:text-emerald-400">{formatRoas(unwrapValue(it.roas ?? it.metrics?.roas), "N/A")}</td>
                  <td className="py-2.5 pr-1 text-right">
                    {/* `preview` is a real object ({thumbnail_url, image_url,
                        video_url, permalink_url}), not a URL string — this
                        was passing the whole object as `src`, which renders
                        as a broken image every time. */}
                    {it.preview?.image_url || it.preview?.thumbnail_url ? (
                      <img src={it.preview?.image_url || it.preview?.thumbnail_url} alt="" className="w-7 h-7 rounded object-cover inline-block" />
                    ) : (
                      <span className="w-7 h-7 rounded bg-gray-100 dark:bg-gray-800 inline-flex items-center justify-center">
                        <ImageIcon size={12} className="text-gray-400" />
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdsetAdsTab;
